import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Mail,
  Building2,
  User,
  AlertCircle,
  CheckSquare,
  TrendingUp,
  Plus,
  Loader2,
  Trash2,
  Edit2,
  Check,
  X,
  FileText,
  Euro,
  Clock,
  MessageSquare,
  HelpCircle,
  Calendar,
  CheckCircle2,
  Activity,
  FolderKanban,
  ChevronRight,
  Brain,
  UserCheck,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow, format } from "date-fns";
import { fr } from "date-fns/locale";
import { Link } from "wouter";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface ContactInteraction {
  id: string;
  type: string;
  description: string | null;
  refId: string | null;
  occurredAt: string;
}

interface LinkedProject {
  id: string;
  name: string;
  status: string;
}

interface ContactDetail {
  contact: {
    id: string;
    name: string;
    email: string;
    company: string | null;
    stage: string;
    assignedTo: string | null;
    notes: string | null;
    lastInteractionAt: string | null;
    followUpAt: string | null;
    waitingForWhat: string | null;
    waitingSince: string | null;
    createdAt: string;
    updatedAt: string;
  };
  emails: {
    id: string;
    subject: string | null;
    fromAddress: string | null;
    fromName: string | null;
    snippet: string | null;
    receivedAt: string | null;
    isUnread: boolean;
  }[];
  tasks: {
    id: string;
    title: string;
    status: string;
    priority: string;
    dueDate: string | null;
    projectId: string | null;
  }[];
  deals: Deal[];
  interactions: ContactInteraction[];
  followUp: { daysSince: number; suggestion: string } | null;
  daysSinceInteraction: number | null;
  linkedProjects: LinkedProject[];
}

interface Deal {
  id: string;
  contactId: string;
  title: string;
  amount: number | null;
  currency: string;
  status: string;
  sentAt: string | null;
  expiresAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const STAGE_LABELS: Record<string, string> = {
  prospect: "Prospect",
  active: "En cours",
  client: "Client",
  lost: "Perdu",
};

const STAGE_COLORS: Record<string, string> = {
  prospect: "bg-blue-100 text-blue-700",
  active: "bg-amber-100 text-amber-700",
  client: "bg-green-100 text-green-700",
  lost: "bg-gray-100 text-gray-500",
};

const DEAL_STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  accepted: "Accepté",
  refused: "Refusé",
  expired: "Expiré",
};

const DEAL_STATUS_COLORS: Record<string, string> = {
  pending: "bg-blue-100 text-blue-700",
  accepted: "bg-green-100 text-green-700",
  refused: "bg-red-100 text-red-700",
  expired: "bg-gray-100 text-gray-500",
};

const INTERACTION_ICONS: Record<string, React.ReactNode> = {
  email_received: <Mail className="w-3.5 h-3.5 text-blue-500" />,
  email_sent: <Mail className="w-3.5 h-3.5 text-green-500" />,
  task_created: <CheckSquare className="w-3.5 h-3.5 text-purple-500" />,
  task_delegated: <User className="w-3.5 h-3.5 text-orange-500" />,
  note_added: <FileText className="w-3.5 h-3.5 text-muted-foreground" />,
};

async function fetchContactDetail(id: string): Promise<ContactDetail> {
  const res = await fetch(`${API_BASE}/api/crm/contacts/${id}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Contact introuvable");
  return res.json() as Promise<ContactDetail>;
}

async function updateContact(id: string, body: Record<string, unknown>) {
  const res = await fetch(`${API_BASE}/api/crm/contacts/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Erreur lors de la mise à jour");
  return res.json();
}

async function setWaiting(id: string, body: { waitingForWhat: string | null; followUpAt?: string | null }) {
  const res = await fetch(`${API_BASE}/api/crm/contacts/${id}/waiting`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Erreur");
  return res.json();
}

async function deleteContact(id: string) {
  const res = await fetch(`${API_BASE}/api/crm/contacts/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Erreur lors de la suppression");
}

async function createDeal(
  contactId: string,
  body: {
    title: string;
    amount?: number;
    status: string;
    sentAt?: string;
    expiresAt?: string;
    notes?: string;
  },
): Promise<Deal> {
  const res = await fetch(
    `${API_BASE}/api/crm/contacts/${contactId}/deals`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw new Error("Erreur lors de la création du devis");
  return res.json() as Promise<Deal>;
}

async function updateDeal(id: string, body: Record<string, unknown>) {
  const res = await fetch(`${API_BASE}/api/crm/deals/${id}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Erreur");
  return res.json();
}

async function deleteDeal(id: string) {
  const res = await fetch(`${API_BASE}/api/crm/deals/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Erreur");
}

function WaitingForSection({
  contactId,
  waitingForWhat,
  waitingSince,
  followUpAt,
  onRefresh,
}: {
  contactId: string;
  waitingForWhat: string | null;
  waitingSince: string | null;
  followUpAt: string | null;
  onRefresh: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(waitingForWhat ?? "");
  const [followUpDate, setFollowUpDate] = useState(
    followUpAt ? followUpAt.slice(0, 10) : "",
  );
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: (body: { waitingForWhat: string | null; followUpAt?: string | null }) =>
      setWaiting(contactId, body),
    onSuccess: () => {
      onRefresh();
      setEditing(false);
      toast({ title: "Mis à jour" });
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });

  const handleSave = () => {
    mutation.mutate({
      waitingForWhat: text.trim() || null,
      followUpAt: followUpDate ? `${followUpDate}T00:00:00.000Z` : null,
    });
  };

  const handleClear = () => {
    mutation.mutate({ waitingForWhat: null, followUpAt: null });
    setText("");
    setFollowUpDate("");
  };

  return (
    <div className="border-t border-border pt-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <HelpCircle className="w-3.5 h-3.5" />
          En attente de
        </span>
        {!editing ? (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-xs"
            onClick={() => {
              setText(waitingForWhat ?? "");
              setFollowUpDate(followUpAt ? followUpAt.slice(0, 10) : "");
              setEditing(true);
            }}
          >
            <Edit2 className="w-3 h-3 mr-1" />
            {waitingForWhat ? "Modifier" : "Définir"}
          </Button>
        ) : (
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs"
              onClick={handleSave}
              disabled={mutation.isPending}
            >
              <Check className="w-3 h-3 mr-1" />
              Sauvegarder
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs"
              onClick={() => setEditing(false)}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Ex : validation du contrat, retour sur le devis…"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <Input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              className="h-8 text-xs"
            />
            <span className="text-xs text-muted-foreground">Relance prévue</span>
          </div>
          {waitingForWhat && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-destructive/70 hover:text-destructive"
              onClick={handleClear}
              disabled={mutation.isPending}
            >
              <X className="w-3 h-3 mr-1" />
              Effacer
            </Button>
          )}
        </div>
      ) : waitingForWhat ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800 font-medium">{waitingForWhat}</p>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-amber-600/80">
            {waitingSince && (
              <span>
                Depuis{" "}
                {formatDistanceToNow(new Date(waitingSince), {
                  locale: fr,
                  addSuffix: true,
                })}
              </span>
            )}
            {followUpAt && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Relance prévue le {format(new Date(followUpAt), "d MMM", { locale: fr })}
              </span>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground/50 italic">
          Rien en attente. Cliquez sur Définir pour suivre ce que vous attendez.
        </p>
      )}
    </div>
  );
}

function CreateDealDialog({
  contactId,
  open,
  onClose,
}: {
  contactId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("pending");
  const [sentAt, setSentAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: Parameters<typeof createDeal>[1]) =>
      createDeal(contactId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-contact", contactId] });
      toast({ title: "Devis créé" });
      setTitle("");
      setAmount("");
      setStatus("pending");
      setSentAt("");
      setExpiresAt("");
      setNotes("");
      onClose();
    },
    onError: () =>
      toast({ title: "Erreur", variant: "destructive" }),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    mutation.mutate({
      title: title.trim(),
      amount: amount ? Number(amount) : undefined,
      status,
      sentAt: sentAt || undefined,
      expiresAt: expiresAt || undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveau devis / opportunité</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label>Titre *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Devis maintenance 2025"
              required
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Montant (€)</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                min="0"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Statut</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="accepted">Accepté</SelectItem>
                  <SelectItem value="refused">Refusé</SelectItem>
                  <SelectItem value="expired">Expiré</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date d'envoi</Label>
              <Input
                type="date"
                value={sentAt ? sentAt.slice(0, 10) : ""}
                onChange={(e) =>
                  setSentAt(e.target.value ? `${e.target.value}T00:00:00.000Z` : "")
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label>Expiration</Label>
              <Input
                type="date"
                value={expiresAt ? expiresAt.slice(0, 10) : ""}
                onChange={(e) =>
                  setExpiresAt(
                    e.target.value ? `${e.target.value}T00:00:00.000Z` : "",
                  )
                }
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Conditions, contexte…"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Créer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DealCard({
  deal,
  onRefresh,
}: {
  deal: Deal;
  onRefresh: () => void;
}) {
  const { toast } = useToast();
  const [editStatus, setEditStatus] = useState(false);
  const [newStatus, setNewStatus] = useState(deal.status);

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateDeal(deal.id, body),
    onSuccess: () => {
      onRefresh();
      setEditStatus(false);
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteDeal(deal.id),
    onSuccess: () => {
      onRefresh();
      toast({ title: "Devis supprimé" });
    },
  });

  return (
    <div className="border border-border/60 rounded-lg p-3 flex items-start gap-3">
      <FileText className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{deal.title}</span>
          {editStatus ? (
            <div className="flex items-center gap-1">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="h-6 text-xs px-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="accepted">Accepté</SelectItem>
                  <SelectItem value="refused">Refusé</SelectItem>
                  <SelectItem value="expired">Expiré</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => updateMutation.mutate({ status: newStatus })}
              >
                <Check className="w-3 h-3" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                onClick={() => setEditStatus(false)}
              >
                <X className="w-3 h-3" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setEditStatus(true)}
              className={`text-xs px-2 py-0.5 rounded-full font-medium cursor-pointer ${DEAL_STATUS_COLORS[deal.status] ?? "bg-gray-100"}`}
            >
              {DEAL_STATUS_LABELS[deal.status] ?? deal.status}
            </button>
          )}
          {deal.amount != null && (
            <span className="text-xs font-semibold text-foreground flex items-center gap-0.5">
              <Euro className="w-3 h-3" />
              {deal.amount.toLocaleString("fr-FR", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          {deal.sentAt && (
            <span>Envoyé le {format(new Date(deal.sentAt), "d MMM yyyy", { locale: fr })}</span>
          )}
          {deal.expiresAt && (
            <span>Expire le {format(new Date(deal.expiresAt), "d MMM yyyy", { locale: fr })}</span>
          )}
        </div>
        {deal.notes && (
          <p className="text-xs text-muted-foreground">{deal.notes}</p>
        )}
      </div>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 shrink-0 text-destructive/60 hover:text-destructive"
        onClick={() => deleteMutation.mutate()}
        disabled={deleteMutation.isPending}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

export default function ContactDetailPage() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreateDeal, setShowCreateDeal] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["crm-contact", params.id],
    queryFn: () => fetchContactDetail(params.id),
  });

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      updateContact(params.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-contact", params.id] });
      queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
    },
    onError: () => toast({ title: "Erreur", variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteContact(params.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
      navigate("/app/crm");
      toast({ title: "Contact supprimé" });
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["crm-contact", params.id] });
    queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
    queryClient.invalidateQueries({ queryKey: ["crm-follow-ups"] });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">Contact introuvable.</p>
        <Link href="/app/crm">
          <Button variant="link" className="mt-2">
            Retour aux contacts
          </Button>
        </Link>
      </div>
    );
  }

  const { contact, emails, tasks, deals, interactions, followUp, daysSinceInteraction, linkedProjects } = data;

  // Merge emails + tasks into a single timeline sorted by date
  type TimelineItem =
    | { kind: "email"; date: string; payload: (typeof emails)[0] }
    | { kind: "task"; date: string; payload: (typeof tasks)[0] }
    | { kind: "interaction"; date: string; payload: ContactInteraction };

  const timeline: TimelineItem[] = [
    ...emails.map((e) => ({
      kind: "email" as const,
      date: e.receivedAt ?? contact.createdAt,
      payload: e,
    })),
    ...tasks.map((t) => ({
      kind: "task" as const,
      date: t.dueDate ?? contact.createdAt,
      payload: t,
    })),
    ...interactions
      .filter((i) => i.type !== "email_received")
      .map((i) => ({
        kind: "interaction" as const,
        date: i.occurredAt,
        payload: i,
      })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      {/* Back */}
      <Link href="/app/crm">
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Contacts
        </button>
      </Link>

      {/* Follow-up alert */}
      {followUp && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">{followUp.suggestion}</p>
          </div>
        </div>
      )}

      {/* Relational intelligence signals */}
      {(() => {
        const delegatedTasks = tasks.filter((t) => t.status === "delegated");
        const hasSilence =
          (daysSinceInteraction ?? 0) > 30 && linkedProjects.length > 0;
        const hasWaiting = Boolean(contact.waitingForWhat);
        const hasStall = delegatedTasks.length > 0;
        if (!hasSilence && !hasWaiting && !hasStall) return null;

        return (
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Brain className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Signal IA</span>
            </div>

            {hasWaiting && (
              <div className="flex items-start gap-2.5">
                <HelpCircle className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">En attente de ce contact</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {contact.waitingForWhat}
                    {contact.waitingSince && (
                      <span>
                        {" · depuis "}
                        {formatDistanceToNow(new Date(contact.waitingSince), {
                          addSuffix: false,
                          locale: fr,
                        })}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {hasStall && (
              <div className="flex items-start gap-2.5">
                <UserCheck className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">
                    {delegatedTasks.length} tâche{delegatedTasks.length > 1 ? "s déléguées" : " déléguée"} sans réponse
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {delegatedTasks[0].title}
                    {delegatedTasks.length > 1 && ` + ${delegatedTasks.length - 1} autre${delegatedTasks.length > 2 ? "s" : ""}`}
                  </p>
                </div>
              </div>
            )}

            {hasSilence && !hasWaiting && !hasStall && (
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Contact silencieux</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Aucune interaction depuis {daysSinceInteraction} jours — ce contact est lié à{" "}
                    {linkedProjects.length} projet{linkedProjects.length > 1 ? "s" : ""}
                    {linkedProjects[0] && ` (${linkedProjects[0].name})`}.
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Header card */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-xl font-bold text-primary">
                {contact.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold">{contact.name}</h1>
                <Select
                  value={contact.stage}
                  onValueChange={(v) => updateMutation.mutate({ stage: v })}
                >
                  <SelectTrigger className="h-7 text-xs px-2 w-auto">
                    <span
                      className={`px-2 py-0.5 rounded-full font-medium ${STAGE_COLORS[contact.stage] ?? ""}`}
                    >
                      {STAGE_LABELS[contact.stage] ?? contact.stage}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prospect">Prospect</SelectItem>
                    <SelectItem value="active">En cours</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="lost">Perdu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                {contact.company && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 className="w-3.5 h-3.5 shrink-0" />
                    {contact.company}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="w-3.5 h-3.5 shrink-0" />
                  <a href={`mailto:${contact.email}`} className="hover:underline">
                    {contact.email}
                  </a>
                </div>
                {contact.assignedTo && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-3.5 h-3.5 shrink-0" />
                    Assigné à {contact.assignedTo}
                  </div>
                )}
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="shrink-0 text-destructive/50 hover:text-destructive"
              onClick={() => {
                if (confirm("Supprimer ce contact définitivement ?")) {
                  deleteMutation.mutate();
                }
              }}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {/* Interaction stats */}
          <div className="flex items-center gap-4 pt-1 border-t border-border/60 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" />
              {contact.lastInteractionAt ? (
                <span>
                  Dernière interaction{" "}
                  <span className="font-medium text-foreground">
                    {formatDistanceToNow(new Date(contact.lastInteractionAt), {
                      locale: fr,
                      addSuffix: true,
                    })}
                  </span>
                </span>
              ) : (
                <span className="text-muted-foreground/60">Aucune interaction enregistrée</span>
              )}
            </div>
            {daysSinceInteraction !== null && daysSinceInteraction >= 14 && (
              <Badge variant="secondary" className="text-amber-600 bg-amber-50 border-amber-200 text-[10px]">
                {daysSinceInteraction}j sans contact
              </Badge>
            )}
            <div className="flex items-center gap-1.5">
              <CheckSquare className="w-3.5 h-3.5" />
              <span>{tasks.filter(t => t.status === "todo").length} tâche{tasks.filter(t => t.status === "todo").length !== 1 ? "s" : ""} ouverte{tasks.filter(t => t.status === "todo").length !== 1 ? "s" : ""}</span>
            </div>
          </div>

          {/* Waiting for */}
          <WaitingForSection
            contactId={contact.id}
            waitingForWhat={contact.waitingForWhat}
            waitingSince={contact.waitingSince}
            followUpAt={contact.followUpAt}
            onRefresh={refresh}
          />

          {/* Notes */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notes internes
              </span>
              {!editingNotes ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs"
                  onClick={() => {
                    setNotesValue(contact.notes ?? "");
                    setEditingNotes(true);
                  }}
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Modifier
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-xs"
                    onClick={() => {
                      updateMutation.mutate({ notes: notesValue });
                      setEditingNotes(false);
                    }}
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Sauvegarder
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 text-xs"
                    onClick={() => setEditingNotes(false)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
            {editingNotes ? (
              <Textarea
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                rows={3}
                placeholder="Contexte, historique commercial, préférences…"
                autoFocus
              />
            ) : contact.notes ? (
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {contact.notes}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground/50 italic">
                Aucune note. Cliquez sur Modifier pour en ajouter.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Deals */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Devis & opportunités
              {deals.length > 0 && (
                <span className="text-xs font-normal text-muted-foreground">
                  ({deals.length})
                </span>
              )}
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => setShowCreateDeal(true)}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Ajouter
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {deals.length === 0 ? (
            <p className="text-sm text-muted-foreground/50 italic">
              Aucun devis pour ce contact.
            </p>
          ) : (
            <div className="space-y-2">
              {deals.map((d) => (
                <DealCard key={d.id} deal={d} onRefresh={refresh} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Linked projects */}
      {data.linkedProjects && data.linkedProjects.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FolderKanban className="w-4 h-4" />
              Projets liés
              <span className="text-xs font-normal text-muted-foreground">
                ({data.linkedProjects.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1.5">
              {data.linkedProjects.map((p) => (
                <Link key={p.id} href={`/app/projects/${p.id}`}>
                  <div className="flex items-center gap-2.5 p-2.5 rounded-lg border border-border/50 hover:bg-secondary/40 transition-colors cursor-pointer">
                    <FolderKanban className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm flex-1 truncate">{p.name}</span>
                    <Badge
                      variant="secondary"
                      className={`text-[10px] shrink-0 ${p.status === "active" ? "text-green-600 bg-green-50" : "text-muted-foreground"}`}
                    >
                      {p.status === "active" ? "Actif" : p.status === "completed" ? "Terminé" : p.status}
                    </Badge>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Historique
            <span className="text-xs font-normal text-muted-foreground">
              ({timeline.length})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {timeline.length === 0 ? (
            <p className="text-sm text-muted-foreground/50 italic">
              Aucun historique disponible.
            </p>
          ) : (
            <div className="space-y-2">
              {timeline.map((item, idx) => {
                if (item.kind === "email") {
                  const e = item.payload;
                  return (
                    <div
                      key={`email-${e.id}-${idx}`}
                      className="flex items-start gap-3 p-3 rounded-lg border border-border/50 hover:bg-secondary/30 transition-colors"
                    >
                      <Mail className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {e.subject ?? "(sans objet)"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {e.fromName ?? e.fromAddress ?? ""}
                        </p>
                        {e.snippet && (
                          <p className="text-xs text-muted-foreground/70 truncate mt-0.5">
                            {e.snippet}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-[11px] text-muted-foreground">
                          {e.receivedAt
                            ? format(new Date(e.receivedAt), "d MMM yy", { locale: fr })
                            : ""}
                        </span>
                        {e.isUnread && (
                          <span className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                    </div>
                  );
                }
                if (item.kind === "task") {
                  const t = item.payload;
                  const STATUS_ICONS: Record<string, React.ReactNode> = {
                    done: <CheckCircle2 className="w-4 h-4 text-green-500" />,
                    refused: <X className="w-4 h-4 text-destructive/60" />,
                    delegated: <User className="w-4 h-4 text-orange-500" />,
                  };
                  return (
                    <div
                      key={`task-${t.id}-${idx}`}
                      className="flex items-start gap-3 p-3 rounded-lg border border-border/50 hover:bg-secondary/30 transition-colors"
                    >
                      {STATUS_ICONS[t.status] ?? <CheckSquare className="w-4 h-4 text-purple-500 shrink-0 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>
                          {t.title}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">{t.status}</p>
                      </div>
                      {t.dueDate && (
                        <span className="text-[11px] text-muted-foreground shrink-0">
                          {format(new Date(t.dueDate), "d MMM yy", { locale: fr })}
                        </span>
                      )}
                    </div>
                  );
                }
                // Interaction
                const i = item.payload as ContactInteraction;
                return (
                  <div
                    key={`int-${i.id}-${idx}`}
                    className="flex items-start gap-3 p-3 rounded-lg border border-border/40 bg-secondary/20"
                  >
                    <span className="mt-0.5">
                      {INTERACTION_ICONS[i.type] ?? <MessageSquare className="w-3.5 h-3.5 text-muted-foreground" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-muted-foreground">{i.description}</p>
                    </div>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {format(new Date(i.occurredAt), "d MMM yy", { locale: fr })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <CreateDealDialog
        contactId={contact.id}
        open={showCreateDeal}
        onClose={() => setShowCreateDeal(false)}
      />
    </div>
  );
}
